import { useState, useMemo } from 'react'
import {
  Alert,
  Box,
  Card,
  CardHeader,
  CardContent,
  TextField,
  Grid,
  Typography,
  FormControlLabel,
  Switch,
  Slider,
  IconButton,
  InputAdornment,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { MyLocation, Map, Info } from '@mui/icons-material'
import { useAdventureEditorStore, selectCategorySettings } from '../../../stores/adventureEditorStore'
import { DIFFICULTY_LABELS, isGeoCacheCategory, GEOCACHE_RESTRICTIONS, ADVENTURE_MAIN_CATEGORY_LABELS } from '../../../types/adventure-editor.types'
import { GeofencePicker } from '@components/map/GeofencePicker'
import { useCollaborativeAdventureEditor } from '@hooks/useCollaborativeAdventureEditor'
import { usePublishedPermissions } from '../../../hooks/usePublishedPermissions'
import { WithFieldChangeIndicator } from '../FieldChangeIndicator'
import { adventuresApi } from '@api/endpoints/adventures'

// ====================
// TRI-STATE SETTINGS HELPER
// ====================

/**
 * Determines the UI state for a setting based on category configuration.
 * @param categoryAllowed - The category's setting: null = configurable, true = forced ON, false = forced OFF
 * @param currentValue - The current value of the setting
 * @returns Object with disabled state, effective value, and helper text
 */
function getSettingState(
  categoryAllowed: boolean | null | undefined,
  currentValue: boolean,
  categoryName?: string
): {
  disabled: boolean
  value: boolean
  helperText: string | null
} {
  if (categoryAllowed === null || categoryAllowed === undefined) {
    // Configurable - user controls
    return { disabled: false, value: currentValue, helperText: null }
  } else if (categoryAllowed === true) {
    // Forced ON
    return {
      disabled: true,
      value: true,
      helperText: categoryName ? `Required for ${categoryName} adventures` : 'Required by category',
    }
  } else {
    // Forced OFF
    return {
      disabled: true,
      value: false,
      helperText: categoryName ? `Not allowed for ${categoryName} adventures` : 'Not allowed by category',
    }
  }
}

export function SettingsPanel() {
  const generalInfo = useAdventureEditorStore((state) => state.generalInfo)
  const adventureId = useAdventureEditorStore((state) => state.adventureId)
  const updateAdventure = useAdventureEditorStore((state) => state.updateAdventure)
  const categorySettings = useAdventureEditorStore(selectCategorySettings)
  const [mapPickerOpen, setMapPickerOpen] = useState(false)
  const [tempLocation, setTempLocation] = useState<{ latitude: number; longitude: number } | null>(null)
  const [showPublicWarning, setShowPublicWarning] = useState(false)
  const [isDisablingPublic, setIsDisablingPublic] = useState(false)

  // Published permissions - restricts editing based on adventure status and user role
  const {
    isPublished,
    canEditLocation,
    canEditStartDate,
    canEditEndDate,
    canTogglePublic,
    canEditDifficulty,
    canEditMaxParticipants,
    canEditAllowGroups,
    canEditRepeatable,
  } = usePublishedPermissions()

  // Collaborative editing hook - syncs changes with other editors
  const { updateGeneralInfoField } = useCollaborativeAdventureEditor()

  // Check if this is a GeoCache adventure type
  const isGeoCache = isGeoCacheCategory(generalInfo.mainCategory)

  // Get category display name for helper text
  const categoryDisplayName = useMemo(() => {
    if (categorySettings?.displayName) {
      return categorySettings.displayName
    }
    if (generalInfo.mainCategory) {
      return ADVENTURE_MAIN_CATEGORY_LABELS[generalInfo.mainCategory] || 'this category'
    }
    return 'this category'
  }, [categorySettings, generalInfo.mainCategory])

  // Compute tri-state setting states from category settings
  const publicSetting = useMemo(() =>
    getSettingState(categorySettings?.isPublicAllowed, generalInfo.isPublic, categoryDisplayName),
    [categorySettings?.isPublicAllowed, generalInfo.isPublic, categoryDisplayName]
  )

  const groupsSetting = useMemo(() =>
    getSettingState(categorySettings?.groupsAllowed, generalInfo.allowGroups, categoryDisplayName),
    [categorySettings?.groupsAllowed, generalInfo.allowGroups, categoryDisplayName]
  )

  const repeatableSetting = useMemo(() =>
    getSettingState(categorySettings?.repeatableAllowed, generalInfo.isRepeatable, categoryDisplayName),
    [categorySettings?.repeatableAllowed, generalInfo.isRepeatable, categoryDisplayName]
  )

  const handleFieldChange = (field: keyof typeof generalInfo, value: any) => {
    updateGeneralInfoField(field, value)
  }

  const handleGetCurrentLocation = async () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser')
      return
    }

    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        })
      })

      // Update both fields and broadcast
      updateGeneralInfoField('latitude', position.coords.latitude, { immediate: true })
      updateGeneralInfoField('longitude', position.coords.longitude, { immediate: true })
    } catch (error: any) {
      let errorMessage = 'Unable to get current location'

      switch (error.code) {
        case error.PERMISSION_DENIED:
          errorMessage =
            'Location permission denied. Please enable location access in your browser settings.'
          break
        case error.POSITION_UNAVAILABLE:
          errorMessage = 'Location information is unavailable.'
          break
        case error.TIMEOUT:
          errorMessage = 'Location request timed out.'
          break
      }

      alert(errorMessage)
    }
  }

  const handleOpenMapPicker = () => {
    // Initialize temp location with current values if they exist
    if (generalInfo.latitude && generalInfo.longitude) {
      setTempLocation({ latitude: generalInfo.latitude, longitude: generalInfo.longitude })
    } else {
      setTempLocation(null)
    }
    setMapPickerOpen(true)
  }

  const handleMapPickerConfirm = () => {
    if (tempLocation) {
      updateGeneralInfoField('latitude', tempLocation.latitude, { immediate: true })
      updateGeneralInfoField('longitude', tempLocation.longitude, { immediate: true })
    }
    setMapPickerOpen(false)
  }

  const handleMapPickerCancel = () => {
    setMapPickerOpen(false)
    setTempLocation(null)
  }

  const difficultyMarks = [
    { value: 1, label: DIFFICULTY_LABELS[1] },
    { value: 2, label: DIFFICULTY_LABELS[2] },
    { value: 3, label: DIFFICULTY_LABELS[3] },
    { value: 4, label: DIFFICULTY_LABELS[4] },
    { value: 5, label: DIFFICULTY_LABELS[5] },
  ]

  // Handler for public toggle - shows warning when turning off on published adventure
  const handlePublicChange = (checked: boolean) => {
    if (isPublished && !checked) {
      // Show permanent warning before turning off
      setShowPublicWarning(true)
    } else {
      handleFieldChange('isPublic', checked)
    }
  }

  // Confirm disabling public access (permanent action)
  const handleConfirmDisablePublic = async () => {
    if (!adventureId) return

    setIsDisablingPublic(true)
    try {
      // Immediately save to API since this is a permanent change
      await adventuresApi.updateAdventure(adventureId, { isPublic: false } as any)
      // Update local state
      handleFieldChange('isPublic', false)
      updateAdventure({ isPublic: false })
      setShowPublicWarning(false)
    } catch (error) {
      console.error('Failed to disable public access:', error)
      alert('Failed to disable public access. Please try again.')
    } finally {
      setIsDisablingPublic(false)
    }
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Card>
        <CardHeader title="Adventure Settings" />
        <CardContent>
          {/* Location Settings */}
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Location Settings
          </Typography>
          {isPublished && !canEditLocation && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Location settings are locked because this adventure is published.
            </Alert>
          )}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={5}>
              <WithFieldChangeIndicator field="latitude" offset={{ top: 8, right: 48 }}>
                <TextField
                  fullWidth
                  type="number"
                  label="Latitude"
                  value={generalInfo.latitude ?? ''}
                  onChange={(e) => handleFieldChange('latitude', parseFloat(e.target.value) || null)}
                  variant="outlined"
                  disabled={!canEditLocation}
                  inputProps={{
                    step: 0.0000001,
                    min: -90,
                    max: 90,
                  }}
                  InputProps={{
                    endAdornment: canEditLocation ? (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleGetCurrentLocation}
                          edge="end"
                          title="Get Current Location"
                        >
                          <MyLocation />
                        </IconButton>
                      </InputAdornment>
                    ) : undefined,
                  }}
                  helperText={canEditLocation ? 'Used for the location to show on the map' : 'Locked'}
                />
              </WithFieldChangeIndicator>
            </Grid>
            <Grid item xs={12} md={5}>
              <WithFieldChangeIndicator field="longitude" offset={{ top: 8, right: 8 }}>
                <TextField
                  fullWidth
                  type="number"
                  label="Longitude"
                  value={generalInfo.longitude ?? ''}
                  onChange={(e) =>
                    handleFieldChange('longitude', parseFloat(e.target.value) || null)
                  }
                  variant="outlined"
                  disabled={!canEditLocation}
                  inputProps={{
                    step: 0.0000001,
                    min: -180,
                    max: 180,
                  }}
                />
              </WithFieldChangeIndicator>
            </Grid>
            <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <Button
                variant="outlined"
                startIcon={<Map />}
                onClick={handleOpenMapPicker}
                fullWidth
                sx={{ height: 56 }}
                disabled={!canEditLocation}
              >
                Pick on Map
              </Button>
            </Grid>
          </Grid>

          {/* Time Settings */}
          <Typography variant="h6" gutterBottom>
            Time Settings
          </Typography>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <WithFieldChangeIndicator field="startDate" offset={{ top: 8, right: 48 }}>
                <DatePicker
                  label="Valid Start Date"
                  value={generalInfo.startDate}
                  onChange={(date) => handleFieldChange('startDate', date)}
                  disabled={!canEditStartDate}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: 'outlined',
                      helperText: canEditStartDate
                        ? 'When the adventure becomes visible (optional)'
                        : 'Locked: Adventure is published',
                    },
                    field: {
                      clearable: canEditStartDate,
                      onClear: () => handleFieldChange('startDate', null),
                    },
                  }}
                />
              </WithFieldChangeIndicator>
            </Grid>
            <Grid item xs={12} md={6}>
              <WithFieldChangeIndicator field="endDate" offset={{ top: 8, right: 48 }}>
                <DatePicker
                  label="Valid End Date"
                  value={generalInfo.endDate}
                  onChange={(date) => handleFieldChange('endDate', date)}
                  disabled={!canEditEndDate}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: 'outlined',
                      helperText: canEditEndDate
                        ? 'When the adventure expires (optional)'
                        : 'Only the owner or maintainers can modify the end date',
                    },
                    field: {
                      clearable: canEditEndDate,
                      onClear: () => handleFieldChange('endDate', null),
                    },
                  }}
                />
              </WithFieldChangeIndicator>
            </Grid>
          </Grid>

          {/* Basic Settings */}
          <Typography variant="h6" gutterBottom>
            Basic Settings
          </Typography>
          {isGeoCache && (
            <Alert severity="info" sx={{ mb: 2 }}>
              GeoCaches are always public, non-repeatable, and do not support group participation.
            </Alert>
          )}
          {categorySettings && !isGeoCache && (publicSetting.disabled || groupsSetting.disabled || repeatableSetting.disabled) && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Some settings are controlled by the {categoryDisplayName} category configuration.
            </Alert>
          )}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={4}>
              <WithFieldChangeIndicator field="isPublic" offset={{ top: 0, right: 0 }}>
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isGeoCache ? GEOCACHE_RESTRICTIONS.isPublic : publicSetting.value}
                        onChange={(e) => handlePublicChange(e.target.checked)}
                        color="primary"
                        disabled={isGeoCache || publicSetting.disabled || !canTogglePublic}
                      />
                    }
                    label="Public Adventure"
                  />
                  {isGeoCache && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      GeoCaches are always public.
                    </Typography>
                  )}
                  {!isGeoCache && publicSetting.helperText && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {publicSetting.helperText}
                    </Typography>
                  )}
                  {!isGeoCache && !publicSetting.disabled && isPublished && !generalInfo.isPublic && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Public access has been disabled permanently.
                    </Typography>
                  )}
                  {!isGeoCache && !publicSetting.disabled && isPublished && generalInfo.isPublic && (
                    <Typography variant="caption" color="warning.main" display="block">
                      Warning: Disabling public access is permanent.
                    </Typography>
                  )}
                </Box>
              </WithFieldChangeIndicator>
            </Grid>
            <Grid item xs={12} md={4}>
              <WithFieldChangeIndicator field="allowGroups" offset={{ top: 0, right: 0 }}>
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isGeoCache ? GEOCACHE_RESTRICTIONS.allowGroups : groupsSetting.value}
                        onChange={(e) => handleFieldChange('allowGroups', e.target.checked)}
                        color="primary"
                        disabled={isGeoCache || groupsSetting.disabled || !canEditAllowGroups}
                      />
                    }
                    label="Allow Groups"
                  />
                  {isGeoCache && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      GeoCaches do not support groups.
                    </Typography>
                  )}
                  {!isGeoCache && groupsSetting.helperText && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {groupsSetting.helperText}
                    </Typography>
                  )}
                </Box>
              </WithFieldChangeIndicator>
            </Grid>
            <Grid item xs={12} md={4}>
              <WithFieldChangeIndicator field="isRepeatable" offset={{ top: 0, right: 0 }}>
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isGeoCache ? GEOCACHE_RESTRICTIONS.isRepeatable : repeatableSetting.value}
                        onChange={(e) => handleFieldChange('isRepeatable', e.target.checked)}
                        color="primary"
                        disabled={isGeoCache || repeatableSetting.disabled || !canEditRepeatable}
                      />
                    }
                    label="Repeatable Adventure"
                  />
                  {isGeoCache && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      GeoCaches are non-repeatable.
                    </Typography>
                  )}
                  {!isGeoCache && repeatableSetting.helperText && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {repeatableSetting.helperText}
                    </Typography>
                  )}
                </Box>
              </WithFieldChangeIndicator>
            </Grid>

            {/* Difficulty Slider - Hidden for Silent Adventures */}
            {categorySettings?.id !== 10 && (
              <Grid item xs={12} md={6}>
                <WithFieldChangeIndicator field="difficultyLevel" offset={{ top: 0, right: 0 }}>
                  <Box sx={{ px: 2 }}>
                    <Typography gutterBottom>
                      Difficulty Level: {DIFFICULTY_LABELS[generalInfo.difficultyLevel]}
                    </Typography>
                    <Slider
                      value={generalInfo.difficultyLevel}
                      onChange={(e, value) => handleFieldChange('difficultyLevel', value as number)}
                      min={1}
                      max={5}
                      step={1}
                      marks={difficultyMarks}
                      valueLabelDisplay="auto"
                      color="primary"
                      disabled={!canEditDifficulty}
                    />
                    {!canEditDifficulty && (
                      <Typography variant="caption" color="text.secondary">
                        Locked: Adventure is published
                      </Typography>
                    )}
                  </Box>
                </WithFieldChangeIndicator>
              </Grid>
            )}

            {/* Max Participants */}
            <Grid item xs={12} md={6}>
              <WithFieldChangeIndicator field="maxParticipants" offset={{ top: 8, right: 8 }}>
                <TextField
                  fullWidth
                  type="number"
                  label="Max Participants (0 = unlimited)"
                  value={generalInfo.maxParticipants}
                  onChange={(e) =>
                    handleFieldChange('maxParticipants', parseInt(e.target.value) || 0)
                  }
                  variant="outlined"
                  disabled={!canEditMaxParticipants || isGeoCache || !groupsSetting.value}
                  inputProps={{
                    min: 0,
                  }}
                  helperText={
                    !canEditMaxParticipants
                      ? 'Locked: Adventure is published'
                      : isGeoCache || !groupsSetting.value
                        ? 'Groups must be enabled to set max participants'
                        : ''
                  }
                />
              </WithFieldChangeIndicator>
            </Grid>
          </Grid>

        </CardContent>
      </Card>

      {/* Map Picker Dialog */}
      <Dialog
        open={mapPickerOpen}
        onClose={handleMapPickerCancel}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Pick Location on Map</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Click on the map to select the adventure's location. This will be shown as the starting point on the adventure map.
          </Typography>
          <GeofencePicker
            center={tempLocation ?? undefined}
            radiusMiles={0}
            onChange={setTempLocation}
            height="400px"
            showRadius={false}
          />
          {tempLocation && (
            <Typography variant="body2" sx={{ mt: 2 }}>
              Selected: {tempLocation.latitude.toFixed(6)}, {tempLocation.longitude.toFixed(6)}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleMapPickerCancel}>Cancel</Button>
          <Button
            onClick={handleMapPickerConfirm}
            variant="contained"
            disabled={!tempLocation}
          >
            Confirm Location
          </Button>
        </DialogActions>
      </Dialog>

      {/* Public Access Warning Dialog */}
      <Dialog
        open={showPublicWarning}
        onClose={() => setShowPublicWarning(false)}
      >
        <DialogTitle>Disable Public Access?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>This action is permanent.</strong>
            <br /><br />
            Disabling public access on a published adventure cannot be undone.
            The adventure will no longer be visible to new users, but existing
            participants will still have access.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPublicWarning(false)} disabled={isDisablingPublic}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDisablePublic}
            color="error"
            variant="contained"
            disabled={isDisablingPublic}
          >
            {isDisablingPublic ? 'Disabling...' : 'Disable Public Access'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  )
}
